import { Accordion, Body3, H2 } from '@cointracker/styleguide';
import {
  ListItem,
  OrderedList,
  UnorderedList,
} from '@cointracker/styleguide/src/AccordionModule/List';
import { Grid } from '@cointracker/styleguide/src/Grid';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { root } from './FAQAccordion.css';

export interface FAQAccordionProps {
  className?: string;
  children?: ReactNode;
}

export const FAQAccordion = (props: FAQAccordionProps) => {
  const { className, children, ...rest } = props;

  return (
    <Grid className={classNames(root, className)} {...rest}>
      <Accordion.Root heading={<H2>FAQ</H2>}>
        {children || (
          <>
            <Accordion.Item
              value="what-is"
              headline="What is CoinTracker and how does it work?"
            >
              <Body3 variant="secondary">
                CoinTracker is a comprehensive solution for managing
                cryptocurrency portfolios, investments, and tax obligations. It
                helps you seamlessly track your cryptocurrency investments
                across all your exchanges and wallets on one easy-to-use
                platform. By integrating with your cryptocurrency accounts,
                CoinTracker automatically synchronizes your transactions and
                provides you with real-time insights into your portfolio
                performance, asset allocation, and transaction history.
              </Body3>
            </Accordion.Item>

            <Accordion.Item
              value="how-to-connect"
              headline="How do I connect my cryptocurrency exchanges and wallets to CoinTracker?"
            >
              <Body3 variant="secondary">
                Connecting your cryptocurrency exchanges and wallets to
                CoinTracker is easy, fast, and secure:
              </Body3>
              <OrderedList variant="secondary">
                <ListItem>
                  Sign up or sign in to your CoinTracker account
                </ListItem>
                <ListItem>
                  Navigate to &quot;Add Account&quot; and select the exchange or
                  wallet you want to connect
                </ListItem>
                <ListItem>
                  Follow the instructions to connect your account
                </ListItem>
                <ListItem>
                  CoinTracker will automatically import your transaction history
                  and calculate your balances
                </ListItem>
              </OrderedList>
            </Accordion.Item>

            <Accordion.Item
              value="portfolio-management"
              headline="Can CoinTracker help manage my crypto portfolio?"
            >
              <Body3 variant="secondary">
                CoinTracker provides helpful tools to manage your crypto
                portfolio effectively:
              </Body3>
              <UnorderedList variant="secondary">
                <ListItem>
                  Live portfolio tracking: Monitor the value and performance of
                  your entire crypto portfolio
                </ListItem>
                <ListItem>
                  Detailed analytics: Get insights into your holdings,
                  transaction history, and asset allocation
                </ListItem>
                <ListItem>
                  Performance metrics: Track gains, losses, and returns over
                  different time periods
                </ListItem>
                <ListItem>
                  Custom alerts: Set up price alerts to stay informed about
                  market movements
                </ListItem>
              </UnorderedList>
            </Accordion.Item>

            <Accordion.Item
              value="tax-help"
              headline="Can CoinTracker help with my crypto taxes?"
            >
              <Body3 variant="secondary">
                CoinTracker is the best solution for crypto taxes. Over 2.5M+
                customers trust CoinTracker for many reasons:
              </Body3>
              <UnorderedList variant="secondary">
                <ListItem>
                  Automate the process of calculating your tax obligations
                </ListItem>
                <ListItem>
                  Generate tax reports: CoinTracker provides detailed tax
                  reports, including Form 8949, that you can use to file your
                  crypto taxes
                </ListItem>
                <ListItem>
                  Handling various transactions: CoinTracker supports different
                  types of transactions, including trades, transfers, mining
                  income, staking rewards, and airdrops
                </ListItem>
                <ListItem>
                  Tracking compliance updates: CoinTracker ensures that your tax
                  reports are compliant with the latest tax regulations
                </ListItem>
              </UnorderedList>
            </Accordion.Item>

            <Accordion.Item
              value="supported-cryptos"
              headline="What type of cryptocurrencies are supported by CoinTracker?"
            >
              <Body3 variant="secondary">
                CoinTracker supports tracking for 30k+ different
                cryptocurrencies. This extensive coverage ensures that you can
                manage and monitor all your crypto assets easily and accurately.
              </Body3>
            </Accordion.Item>

            <Accordion.Item
              value="security"
              headline="Is CoinTracker safe to use with my cryptocurrency accounts?"
            >
              <Body3 variant="secondary">
                CoinTracker is designed with security at every step. We use
                industry-leading encryption to protect your information, and we
                don&apos;t have the ability to transfer, sell, or buy your
                crypto. CoinTracker uses read-only access to your wallets and
                exchanges. Plus, we adhere to strict privacy policies to ensure
                that your personal and financial information remains secure.
              </Body3>
            </Accordion.Item>
          </>
        )}
      </Accordion.Root>
    </Grid>
  );
};
